import { useMemo } from 'react';
import { t } from 'ttag';
import { FORM_ERROR } from 'final-form';
import {
  FormProps,
  withTypes,
} from 'react-final-form';

import { getFieldsSingleError } from '@utils/common';
import {
  notifyError,
  notifySuccess,
} from '@utils/notifications';
import {
  PromoActionTypeEnum,
  PromoConditionType,
} from '@models';
import { PromoActionFormModal, FormValues } from '@components/PromoActionFormModal';

import {
  useAddPromoActionForProductsMutation,
  useCreateOrUpdatePromoActionMutation,
  usePromoActionFormModalInfoQuery,
  usePromoActionFromModalProductDetailsInfoQuery,
  useRemovePromoActionForProductsMutation,
} from './gql/graphql.v3.generated';

const getErrorCodeText = () => ({
  already_adding_removing: t`Товар буде прибрано з акції через декілька хвилин`,
});

interface Error {
  code: 'already_adding_removing',
  message: string,
}

interface Props {
  promoType: PromoActionTypeEnum,
  isAllProducts?: boolean,
  productsIds: number[],
  isOpen: boolean,
  onCreateOrUpdateSuccess?: () => void,
  onRemoveSuccess?: () => void,
  onClose: () => void,
}

export const PromoActionFormModalContainer: React.FC<Props> = ({
  promoType,
  isAllProducts,
  productsIds,
  isOpen,
  onCreateOrUpdateSuccess,
  onRemoveSuccess,
  onClose,
}) => {
  const { Form } = withTypes<FormValues>();

  const { data: productDetailsData } = usePromoActionFromModalProductDetailsInfoQuery({
    context: {
      batch: true,
    },
    variables: {
      productId: productsIds[0],
    },
    // fetch if only one product selected
    skip: productsIds.length !== 1 || isAllProducts,
  });

  const { data: promoActionData } = usePromoActionFormModalInfoQuery({
    context: {
      batch: true,
    },
  });

  const [createOrUpdatePromoAction] = useCreateOrUpdatePromoActionMutation();
  const [addPromoActionForProducts] = useAddPromoActionForProductsMutation();
  const [removePromoAction] = useRemovePromoActionForProductsMutation();

  const promoAction = promoActionData?.promoActions?.find(
    (promo) => promo.promoType === promoType,
  );
  const isProductInPromoAction = productDetailsData?.productDetails.promoActions?.find(
    (promo) => promo.promoType === promoType,
  )?.isAdded;
  const isPromoActionActive = promoAction?.conditions.some((condition) => !!condition.isSelected);
  const [includeIds, excludeIds] = isAllProducts
    ? [null, productsIds]
    : [productsIds, null];
  const allProducts = (includeIds?.length || excludeIds?.length)
    ? null : isAllProducts;

  const activeCondition = useMemo(
    () => {
      if (promoAction) {
        const selectedCondition = promoAction.conditions.find(
          (condition) => !!condition.isSelected,
        );

        if (selectedCondition) {
          return {
            type: selectedCondition.type,
            value: selectedCondition.value || undefined,
          };
        }
      }

      return null;
    },
    [promoAction],
  );

  const initialValues = useMemo(
    () => (
      activeCondition
        ? { condition: activeCondition }
        : {
          condition: {
            type: PromoConditionType.Empty,
            value: undefined,
          },
        }
    ),
    [activeCondition],
  );

  const handleSubmit: FormProps<FormValues>['onSubmit'] = async (values) => {
    if (!values.condition) {
      return {
        [FORM_ERROR]: t`Додайте умови для акції`,
      };
    }

    const { data: createOrUpdatePromoActionData } = await createOrUpdatePromoAction({
      variables: {
        condition: values.condition,
        promoType: PromoActionTypeEnum.FreeShipping,
      },
    });

    const createOrUpdatePromoActionErrors = (
      createOrUpdatePromoActionData?.createOrUpdatePromoAction?.errors
    );

    if (createOrUpdatePromoActionErrors) {
      const fieldsSingleError = getFieldsSingleError(createOrUpdatePromoActionErrors);

      return {
        ...fieldsSingleError,
        [FORM_ERROR]: fieldsSingleError.__all__ || fieldsSingleError.promoType,
      };
    }

    if (!isProductInPromoAction) {
      const { data: addPromoActionForProductsData } = await addPromoActionForProducts({
        variables: {
          promoType: PromoActionTypeEnum.FreeShipping,
          allProducts,
          excludeIds,
          includeIds,
        },
      });

      const addPromoActionForProductsErrors = (
        addPromoActionForProductsData?.addPromoActionForProducts?.errors
      );

      if (addPromoActionForProductsErrors) {
        const fieldsSingleError = getFieldsSingleError(addPromoActionForProductsErrors);
        return {
          ...fieldsSingleError,
          [FORM_ERROR]: fieldsSingleError.__all__
          || fieldsSingleError.includeIds
          || fieldsSingleError.promoType,
        };
      }
    }

    const successMessage = isPromoActionActive ? t`Речі додані в акцію. Умови оновлено для всіх речей` : t`Речі додані в акцію.`;

    notifySuccess(successMessage);

    if (onCreateOrUpdateSuccess) {
      onCreateOrUpdateSuccess();
    }

    return null;
  };

  const handleRemovePromoAction = async () => {
    const response = await removePromoAction({
      variables: {
        promoType: PromoActionTypeEnum.FreeShipping,
        allProducts,
        excludeIds,
        includeIds,
      },
    });

    const errors = response.data?.removePromoActionForProducts?.errors;

    if (errors) {
      const formError = errors.find(({ field }) => field === '__all__');

      if (formError) {
        const err = formError.messages[0] as Error;
        const message = getErrorCodeText()[err.code];

        if (message) {
          onClose();
          notifyError(message);
          return;
        }
      }

      const fieldsSingleError = getFieldsSingleError(errors);

      if (fieldsSingleError.__all__) {
        notifyError(fieldsSingleError.__all__);
      }
    } else {
      notifySuccess(
        (productsIds.length > 1 || isAllProducts) ? t`Товари прибрані з акції` : t`Товар видалено з акції`,
      );

      if (onRemoveSuccess) {
        onRemoveSuccess();
      }
    }
  };

  if (promoAction) {
    return (
      <Form
        initialValues={initialValues}
        onSubmit={handleSubmit}
        render={
          () => (
            <PromoActionFormModal
              isOpen={isOpen}
              title={promoAction.title}
              description={promoAction.description}
              important={promoAction.important}
              isActionActive={!!isProductInPromoAction}
              onRequestClose={onClose}
              onRequestRemovePromoAction={handleRemovePromoAction}
            />
          )
        }
      />
    );
  }

  return null;
};
